import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  Alert,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Helmet } from 'react-helmet-async';

import config from '@src/config';
import paths from '@src/paths';
import { toast } from '@src/components/core/toaster';
import { fetchClient } from '@src/api/actions/clients';
import leadGurusLogo from '@src/assets/logo-full.png';
import {
  fetchProjectionSummary,
  fetchProjectionZips,
  fetchProjectionZipDemographics,
  createProjectionShare,
  fetchProjectionDraft,
  saveProjectionDraft,
  fetchProjectionKpiOverrides,
  saveProjectionKpiOverrides,
} from '@src/api/actions/projections';
import { fetchVerticals } from '@src/api/actions/verticals';

const ZONE_TEMPLATE_ROWS = [
  { zip: '60601', territory: 'Downtown' },
  { zip: '60614', territory: 'North' },
];

const DEFAULT_STATE = {
  territories: {
    text: '',
    fileName: '',
    parsed: [],
  },
  verticals: {
    selections: [],
    applyToAllZips: true,
    budgets: {},
  },
  projection: {
    scenario: 'base',
    budget: 0,
    summary: null,
    zipRows: [],
  },
  replaceExistingZips: true,
};

function parseZips(text) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [zip = '', territory = ''] = line.split(/[,|\t]/);
      let normalizedZip = zip.trim();
      if (/^\d{4}$/.test(normalizedZip)) {
        normalizedZip = `0${normalizedZip}`;
      }
      return { zip: normalizedZip, territory: territory.trim() };
    })
    .filter((row) => row.zip);
}

const normalizeZip = (zip) => {
  let normalized = String(zip || '').trim();
  if (/^\d{4}$/.test(normalized)) {
    normalized = `0${normalized}`;
  }
  return normalized;
};

function PublicProjectionsPage() {
  const { clientSlug = 'new-client' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(DEFAULT_STATE);
  const [client, setClient] = useState(null);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [isClientError, setIsClientError] = useState(false);
  const [isLoadingProjection, setIsLoadingProjection] = useState(false);
  const [isLoadingVerticals, setIsLoadingVerticals] = useState(false);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [demographics, setDemographics] = useState({ territories: [], totals: null, meta: null, features: [] });
  const scenarioMultipliers = useMemo(() => ({ low: 0.9, base: 1, high: 1.2 }), []);
  const [shareToken, setShareToken] = useState('');
  const hasHydratedState = useRef(false);
  const draftSaveTimer = useRef(null);
  const [kpiOverrides, setKpiOverrides] = useState({});
  const [isLoadingKpis, setIsLoadingKpis] = useState(false);
  const [isSavingKpis, setIsSavingKpis] = useState(false);

  useEffect(() => {
    setState((prev) => ({ ...prev, projection: { ...DEFAULT_STATE.projection } }));
    if (!clientSlug) {
      setClient(null);
      return;
    }
    setIsLoadingClient(true);
    setIsClientError(false);
    fetchClient(clientSlug)
      .then((response) => {
        setClient(response?.data || null);
      })
      .catch(() => {
        setClient(null);
        setIsClientError(true);
      })
      .finally(() => setIsLoadingClient(false));
  }, [clientSlug]);

  useEffect(() => {
    if (!clientSlug) return;
    fetchProjectionDraft(clientSlug)
      .then((resp) => {
        const draft = resp?.data || {};
        const payload = draft.payload || {};
        setState((prev) => ({
          ...prev,
          ...payload,
          territories: { ...prev.territories, ...(payload.territories || {}) },
          verticals: { ...prev.verticals, ...(payload.verticals || {}) },
          projection: {
            ...prev.projection,
            ...(payload.projection || {}),
            summary: draft.projection_summary || prev.projection.summary,
            zipRows: draft.projection_zip_rows || prev.projection.zipRows,
          },
        }));
        if (draft.projection_demographics) {
          setDemographics(draft.projection_demographics);
        }
      })
      .finally(() => {
        hasHydratedState.current = true;
      });
  }, [clientSlug]);

  const buildDraftPayload = () => ({
    territories: state.territories,
    verticals: state.verticals,
    projection: {
      scenario: state.projection.scenario,
      budget: state.projection.budget,
    },
    replaceExistingZips: state.replaceExistingZips,
  });

  useEffect(() => {
    if (!clientSlug || !hasHydratedState.current) return;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => {
      const payload = buildDraftPayload();
      saveProjectionDraft(clientSlug, {
        payload,
        projection_summary: state.projection.summary,
        projection_zip_rows: state.projection.zipRows,
        projection_demographics: demographics,
      }).catch(() => {});
    }, 800);
    return () => {
      if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    };
  }, [clientSlug, demographics, state]);

  useEffect(() => {
    if (client?.slug && clientSlug && clientSlug !== client.slug) {
      navigate(`/projections/${client.slug}${location.search}`, { replace: true });
    }
  }, [client?.slug, clientSlug, location.search, navigate]);

  useEffect(() => {
    const zipsParam = searchParams.get('zips');
    const verticalsParam = searchParams.get('verticals');
    const budgetParam = searchParams.get('budget');
    const scenarioParam = searchParams.get('scenario');

    setState((prev) => {
      const next = { ...prev, verticals: { ...prev.verticals }, projection: { ...prev.projection } };
      if (verticalsParam) {
        next.verticals.selections = verticalsParam.split(',').filter(Boolean);
      }
      if (budgetParam) {
        next.projection.budget = Number(budgetParam) || prev.projection.budget;
      }
      if (scenarioParam) {
        next.projection.scenario = scenarioParam;
      }
      if (zipsParam) {
        try {
          const parsed = JSON.parse(zipsParam);
          const text = parsed
            .map((row) => [normalizeZip(row.zip), row.territory].filter(Boolean).join(', '))
            .join('\n');
          const normalizedParsed = parsed.map((row) => ({
            ...row,
            zip: normalizeZip(row.zip),
          }));
          next.territories = { ...prev.territories, parsed: normalizedParsed, text };
        } catch (e) {
          // ignore malformed
        }
      }
      return next;
    });
  }, [searchParams]);

  useEffect(() => {
    const selected = (state.verticals.selections || []).filter(Boolean);
    if (!clientSlug || !selected.length) {
      setKpiOverrides({});
      return;
    }
    setIsLoadingKpis(true);
    fetchProjectionKpiOverrides(clientSlug, selected)
      .then((resp) => {
        const rows = resp?.data?.overrides || [];
        const next = {};
        rows.forEach((row) => {
          next[row.vertical] = {
            assumed_cpl: row.assumed_cpl ?? '',
            set_rate: row.set_rate != null ? row.set_rate * 100 : '',
            issue_rate: row.issue_rate != null ? row.issue_rate * 100 : '',
            demo_rate: row.demo_rate != null ? row.demo_rate * 100 : '',
            net_close_rate: row.net_close_rate != null ? row.net_close_rate * 100 : '',
            avg_job_size: row.avg_job_size ?? '',
            is_override: row.is_override,
          };
        });
        setKpiOverrides(next);
      })
      .finally(() => setIsLoadingKpis(false));
  }, [clientSlug, state.verticals.selections]);

  useEffect(() => {
    setIsLoadingVerticals(true);
    fetchVerticals()
      .then((response) => {
        setVerticalOptions(response?.data || []);
      })
      .catch(() => {
        toast.error('Could not load verticals. Please refresh and try again.');
      })
      .finally(() => setIsLoadingVerticals(false));
  }, []);

  const updateState = (path, value) => {
    setState((prev) => {
      const next = { ...prev };
      const segments = path.split('.');
      let cursor = next;
      segments.slice(0, -1).forEach((segment) => {
        cursor[segment] = { ...cursor[segment] };
        cursor = cursor[segment];
      });
      cursor[segments.slice(-1)[0]] = value;
      return next;
    });
  };

  const handleZipTextChange = (value) => {
    setState((prev) => ({
      ...prev,
      territories: {
        ...prev.territories,
        text: value,
        parsed: parseZips(value),
      },
    }));
  };

  const handleZipFile = (files) => {
    if (!files || !files.length) return;
    const [file] = files;
    const reader = new FileReader();
    reader.onload = (event) => {
      const contents = event.target?.result || '';
      setState((prev) => ({
        ...prev,
        territories: {
          ...prev.territories,
          text: contents,
          parsed: parseZips(String(contents)),
          fileName: file.name,
        },
      }));
    };
    reader.readAsText(file);
  };

  const buildProjectionPayload = () => {
    const selectedVerticals = (state.verticals.selections || []).filter(Boolean);
    const baseZipRows = state.territories.parsed || [];
    if (!selectedVerticals.length) return null;
    const zipRows =
      selectedVerticals.length === 0
        ? baseZipRows.map((row) => ({ zip: normalizeZip(row.zip), territory: row.territory }))
        : baseZipRows.flatMap((row) =>
            selectedVerticals.map((vertical) => ({
              zip: normalizeZip(row.zip),
              territory: row.territory,
              vertical,
            }))
          );
    const zipList = Array.from(new Set(zipRows.map((row) => row.zip))).filter(Boolean);
    const verticalBudgets = {};
    selectedVerticals.forEach((v) => {
      verticalBudgets[v] = Number(state.verticals.budgets?.[v] ?? 0);
    });
    const totalBudget = Object.values(verticalBudgets).reduce((sum, val) => sum + (Number(val) || 0), 0);

    return {
      verticals: selectedVerticals,
      zips: zipList,
      zip_rows: zipRows,
      budget: totalBudget || state.projection.budget,
      vertical_budgets: verticalBudgets,
      scenario: state.projection.scenario || 'base',
      vertical: selectedVerticals[0] || '',
      client_slug: clientSlug,
      replace_existing_zip_data: !!state.replaceExistingZips,
    };
  };

  const buildKpiOverridePayload = () => {
    const selectedVerticals = (state.verticals.selections || []).filter(Boolean);
    if (!selectedVerticals.length) return [];
    return selectedVerticals.map((vertical) => {
      const entry = kpiOverrides[vertical] || {};
      const toNumber = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
      };
      const pct = (value) => {
        const num = toNumber(value);
        return num == null ? null : num / 100;
      };
      return {
        vertical,
        assumed_cpl: toNumber(entry.assumed_cpl),
        set_rate: pct(entry.set_rate),
        issue_rate: pct(entry.issue_rate),
        demo_rate: pct(entry.demo_rate),
        net_close_rate: pct(entry.net_close_rate),
        avg_job_size: toNumber(entry.avg_job_size),
      };
    });
  };

  const buildSharePayload = (summary, zipRows, demo) => ({
    ...buildProjectionPayload(),
    projection_summary: summary,
    projection_zip_rows: zipRows,
    projection_demographics: demo,
    replace_existing_zip_data: !!state.replaceExistingZips,
    client_name: client?.name,
    client_logo_url: client?.client_logo_url,
    client_logo_file: client?.client_logo_file,
  });

  const handleProjectionFetch = () => {
    setShareToken('');
    setIsLoadingProjection(true);
    const payload = buildProjectionPayload();
    if (!payload) {
      setIsLoadingProjection(false);
      return;
    }
    const kpiOverridesPayload = buildKpiOverridePayload();
    const kpiSave = kpiOverridesPayload.length
      ? saveProjectionKpiOverrides(clientSlug, kpiOverridesPayload).catch(() => {})
      : Promise.resolve();
    kpiSave.then(() =>
      Promise.all([fetchProjectionSummary(payload), fetchProjectionZips(payload), fetchProjectionZipDemographics(payload)])
      .then(([summaryResp, zipResp, demoResp]) => {
        const summary = summaryResp?.data?.summary || null;
        const zipRows = zipResp?.data?.rows || [];
        const demo = {
          territories: demoResp?.data?.territories || [],
          totals: demoResp?.data?.totals || null,
          meta: demoResp?.data?.meta || null,
          features: demoResp?.data?.features || [],
        };
        setState((prev) => ({
          ...prev,
          projection: {
            ...prev.projection,
            summary,
            zipRows,
          },
        }));
        setDemographics(demo);
        const sharePayload = buildSharePayload(summary, zipRows, demo);
        return createProjectionShare(sharePayload);
      })
      .then((resp) => {
        const token = resp?.data?.token;
        if (token) {
          setShareToken(token);
        }
      })
      .catch(() => {
        toast.error('Could not load projections. Please try again.');
      })
      .finally(() => {
        setIsLoadingProjection(false);
      })
    );
  };

  const handleKpiChange = (vertical, field, value) => {
    setKpiOverrides((prev) => ({
      ...prev,
      [vertical]: {
        ...(prev[vertical] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveKpis = () => {
    const overrides = buildKpiOverridePayload();
    if (!overrides.length) return;
    setIsSavingKpis(true);
    saveProjectionKpiOverrides(clientSlug, overrides)
      .then(() => toast.success('KPI overrides saved.'))
      .catch(() => toast.error('Could not save KPI overrides.'))
      .finally(() => setIsSavingKpis(false));
  };

  const handleCopyClientView = () => {
    if (!shareUrl) {
      toast.error('Generate projections to create a client link.');
      return;
    }
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => toast.success('Client view link copied.'))
      .catch(() => toast.error('Could not copy the client view link.'));
  };

const verticalRollup = useMemo(() => {
  const totals = new Map();
  (state.projection.zipRows || []).forEach((row) => {
    const key = row.vertical || 'Unknown';
    const current = totals.get(key) || { zipCount: 0, estLeads: 0, avgCpl: 0 };
      current.zipCount += 1;
      current.estLeads += Number(row.est_leads || 0);
      current.avgCpl += Number(row.avg_cpl || 0);
      totals.set(key, current);
    });
    return Array.from(totals.entries()).map(([vertical, data]) => ({
      vertical,
      zipCount: data.zipCount,
      estLeads: data.estLeads,
      avgCpl: data.zipCount ? Math.round((data.avgCpl / data.zipCount) * 100) / 100 : 0,
    }));
  }, [state.projection.zipRows]);

  const territoryRollup = useMemo(() => {
    const counts = new Map();
    (state.territories.parsed || []).forEach((row) => {
      const key = row.territory || 'Unknown';
      const current = counts.get(key) || { zips: [] };
      current.zips.push(row.zip);
      counts.set(key, current);
    });
    return Array.from(counts.entries()).map(([territory, data]) => ({
      territory,
      count: data.zips.length,
      sampleZips: data.zips.slice(0, 6),
    }));
  }, [state.territories.parsed]);

  const territoryRows = useMemo(() => {
    const rows = [];
    const demoMap = new Map();
    (demographics.territories || []).forEach((row) => {
      demoMap.set(row.territory || 'Unknown', row);
    });

    const keys = new Set([...(demographics.territories || []).map((t) => t.territory || 'Unknown')]);
    keys.forEach((key) => {
      const demo = demoMap.get(key);
      rows.push({
        territory: key,
        zipCount: demo?.zip_count ?? 0,
        sampleZips: demo?.sample_zips || [],
        population: demo?.population ?? null,
        populationFiltered: demo?.population_filtered ?? null,
        populationRetainedPct: demo?.population_retained_pct ?? null,
        medianIncome: demo?.median_income ?? null,
        medianIncomeFiltered: demo?.median_income_filtered ?? null,
        medianHomeValue: demo?.median_home_value ?? null,
        medianHomeValueFiltered: demo?.median_home_value_filtered ?? null,
        zipCountFiltered: demo?.zip_count_filtered ?? null,
        minIncome: demo?.min_household_income ?? null,
        minHomeValue: demo?.min_home_value ?? null,
        minSfh: demo?.min_sfh_percentage ?? null,
        minOwnership: demo?.min_home_ownership_percentage ?? null,
      });
    });
    return rows;
  }, [demographics]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  };

  const formatPercent = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return '—';
    return `${num.toFixed(decimals)}%`;
  };

  const formatRate = (value, decimals = 0) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return `${(num * 100).toFixed(decimals)}%`;
  };

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const shareUrl = useMemo(() => {
    if (!shareToken) return '';
    return `${baseUrl}${paths.projections.clientView(clientSlug || 'new-client')}?latest=1`;
  }, [baseUrl, clientSlug, shareToken]);

  const versionedShareUrl = useMemo(() => {
    if (!shareToken) return '';
    return `${baseUrl}${paths.projections.clientView(clientSlug || 'new-client')}?token=${encodeURIComponent(shareToken)}`;
  }, [baseUrl, clientSlug, shareToken]);

  const projectionResults = useMemo(() => {
    const summary = state.projection.summary;
    if (!summary) return { rows: [], totals: null, assumptions: {} };

    const scenarioKey = state.projection.scenario || 'base';
    const multiplier = scenarioMultipliers[scenarioKey] || 1;
    const assumptions = summary.assumptions || {};
    const rows = (summary.verticals || []).map((v) => {
      const budget = Number(v.budget) || 0;
      const baseCpl = Number(v.base_cpl ?? summary.assumed_cpl ?? 0);
      const cpl = baseCpl ? baseCpl * multiplier : 0;
      const setRate = Number(v.set_rate ?? assumptions.set_rate ?? 0);
      const issueRate = Number(v.issue_rate ?? assumptions.issue_rate ?? 0);
      const demoRate = Number(v.demo_rate ?? assumptions.demo_rate ?? 0);
      const netCloseRate = Number(v.net_close_rate ?? assumptions.net_close_rate ?? 0);
      const avgJobSize = Number(v.avg_job_size ?? assumptions.avg_job_size ?? 0);
      const leads = cpl ? budget / cpl : 0;
      const setAppointments = leads * setRate;
      const issues = setAppointments * issueRate;
      const demos = issues * demoRate;
      const netSold = demos * netCloseRate;
      const netRevenue = netSold * avgJobSize;
      return {
        name: v.name || 'Unknown',
        budget,
        cpl,
        leads,
        setAppointments,
        issues,
        demos,
        netSold,
        avgJobSize,
        netRevenue,
        setRate,
        issueRate,
        demoRate,
        netCloseRate,
      };
    });

    const totals = rows.reduce(
      (acc, row) => {
        acc.budget += row.budget;
        acc.leads += row.leads;
        acc.setAppointments += row.setAppointments;
        acc.issues += row.issues;
        acc.demos += row.demos;
        acc.netSold += row.netSold;
        acc.netRevenue += row.netRevenue;
        return acc;
      },
      { budget: 0, leads: 0, setAppointments: 0, issues: 0, demos: 0, netSold: 0, netRevenue: 0 }
    );
    totals.avgCpl = totals.leads ? totals.budget / totals.leads : null;
    totals.avgJobSize = Number(summary.assumptions?.avg_job_size ?? rows[0]?.avgJobSize ?? 0) || 0;
    totals.costOfMarketingPct = totals.netRevenue ? totals.budget / totals.netRevenue : null;

    return { rows, totals, assumptions, scenarioMultiplier: multiplier };
  }, [scenarioMultipliers, state.projection.scenario, state.projection.summary]);

  const proceedToOnboarding = () => {
    const params = new URLSearchParams();
    if (state.verticals.selections.length) {
      params.set('verticals', state.verticals.selections.join(','));
    }
    if (state.projection.budget) params.set('budget', state.projection.budget);
    if (state.projection.scenario) params.set('scenario', state.projection.scenario);
    if (state.territories.parsed.length) {
      params.set('zips', JSON.stringify(state.territories.parsed));
    }
    const search = params.toString();
    const targetSlug = client?.slug || clientSlug || '';
    navigate(`/onboarding/${targetSlug}${search ? `?${search}` : ''}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Helmet>
        <title>Projections | {config.SITE.title}</title>
      </Helmet>

      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #f66f39, #f88f5f)',
                borderRadius: 2,
                color: 'common.white',
                p: { xs: 2, md: 3 },
                boxShadow: 4,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ alignItems: { md: 'center', xs: 'flex-start' }, justifyContent: 'space-between' }}
              >
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box
                    component="img"
                    src={leadGurusLogo}
                    alt="Lead Gurus"
                    sx={{
                      height: 64,
                      width: 'auto',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.2))',
                    }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Lead Gurus Projections
                  </Typography>
                  {client?.client_logo_url || client?.client_logo_file ? (
                    <Box
                      component="img"
                      src={client?.client_logo_url || client?.client_logo_file}
                      alt={client?.name || 'Client logo'}
                      sx={{
                        height: 48,
                        width: 'auto',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{ width: 48, height: 48, bgcolor: 'white', color: 'text.primary', fontWeight: 700 }}
                    >
                      {client?.name ? client.name.slice(0, 2).toUpperCase() : 'CL'}
                    </Avatar>
                  )}
                </Stack>
              </Stack>
            </Box>
            {isClientError && (
              <Alert severity="warning">
                Could not load client or you do not have access to this client.
              </Alert>
            )}

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Service areas & verticals
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Paste zip codes and territories"
                      helperText="One per line, format: zip_code, territory"
                      minRows={6}
                      maxRows={8}
                      multiline
                      value={state.territories.text}
                      onChange={(e) => handleZipTextChange(e.target.value)}
                      sx={{ textarea: { overflow: 'auto' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1.5}>
                      <Button component="label" startIcon={<UploadFileIcon />} variant="outlined">
                        Upload zip template
                        <input
                          hidden
                          type="file"
                          accept=".csv,.txt"
                          onChange={(e) => handleZipFile(e.target.files)}
                        />
                      </Button>
                      {state.territories.fileName ? (
                        <Typography variant="body2">Uploaded: {state.territories.fileName}</Typography>
                      ) : (
                        <Typography color="text.secondary" variant="body2">
                          Template columns: zip_code, territory
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Typography color="text.secondary" variant="caption">
                        Example format
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {ZONE_TEMPLATE_ROWS.map((row) => (
                          <Chip key={row.zip} label={`${row.zip} — ${row.territory}`} size="small" />
                        ))}
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      SelectProps={{ multiple: true, disabled: isLoadingVerticals }}
                      label="Verticals to model"
                      value={state.verticals.selections}
                      onChange={(e) => updateState('verticals.selections', e.target.value)}
                      helperText={
                        isLoadingVerticals
                          ? 'Loading verticals…'
                          : 'We’ll use these verticals for projections.'
                      }
                    >
                      {verticalOptions.length ? (
                        verticalOptions.map((vertical) => (
                          <MenuItem key={vertical.id || vertical.vertical} value={vertical.vertical}>
                            {vertical.vertical}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled value="">
                          {isLoadingVerticals ? 'Loading…' : 'No verticals available'}
                        </MenuItem>
                      )}
                    </TextField>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.verticals.applyToAllZips}
                          onChange={(e) => updateState('verticals.applyToAllZips', e.target.checked)}
                        />
                      }
                      label="Apply these verticals to all zip codes"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ alignItems: { sm: 'center', xs: 'flex-start' }, justifyContent: 'space-between' }}
                  >
                    <Typography variant="h6">Projections</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={state.projection.scenario}
                      onChange={(e, value) => value && updateState('projection.scenario', value)}
                    >
                      <ToggleButton value="low">Low</ToggleButton>
                      <ToggleButton value="base">Base</ToggleButton>
                      <ToggleButton value="high">High</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Budgets by vertical</Typography>
                    {state.verticals.selections.length ? (
                      <Grid container spacing={2}>
                        {state.verticals.selections.map((vertical) => (
                          <Grid item xs={12} sm={6} md={4} key={vertical}>
                            <TextField
                              fullWidth
                              type="number"
                              label={`${vertical} budget`}
                              InputProps={{ startAdornment: '$', inputProps: { min: 0, step: 100 } }}
                              value={state.verticals.budgets?.[vertical] ?? ''}
                              onChange={(e) =>
                                updateState('verticals.budgets', {
                                  ...state.verticals.budgets,
                                  [vertical]: e.target.value,
                                })
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        Select at least one vertical to enter budgets.
                      </Typography>
                    )}
                  </Stack>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">KPI overrides (client-specific)</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSaveKpis}
                        disabled={isSavingKpis || !state.verticals.selections.length}
                      >
                        {isSavingKpis ? 'Saving…' : 'Save KPI overrides'}
                      </Button>
                    </Stack>
                    {state.verticals.selections.length ? (
                      <Grid container spacing={2}>
                        {state.verticals.selections.map((vertical) => (
                          <Grid item xs={12} key={`${vertical}-kpis`}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {vertical}
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Assumed CPL"
                                    InputProps={{ startAdornment: '$' }}
                                    value={kpiOverrides[vertical]?.assumed_cpl ?? ''}
                                    onChange={(e) => handleKpiChange(vertical, 'assumed_cpl', e.target.value)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Set rate %"
                                    value={kpiOverrides[vertical]?.set_rate ?? ''}
                                    onChange={(e) => handleKpiChange(vertical, 'set_rate', e.target.value)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Issue rate %"
                                    value={kpiOverrides[vertical]?.issue_rate ?? ''}
                                    onChange={(e) => handleKpiChange(vertical, 'issue_rate', e.target.value)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Demo rate %"
                                    value={kpiOverrides[vertical]?.demo_rate ?? ''}
                                    onChange={(e) => handleKpiChange(vertical, 'demo_rate', e.target.value)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Close rate %"
                                    value={kpiOverrides[vertical]?.net_close_rate ?? ''}
                                    onChange={(e) => handleKpiChange(vertical, 'net_close_rate', e.target.value)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Avg job size"
                                    InputProps={{ startAdornment: '$' }}
                                    value={kpiOverrides[vertical]?.avg_job_size ?? ''}
                                    onChange={(e) => handleKpiChange(vertical, 'avg_job_size', e.target.value)}
                                  />
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        Select at least one vertical to adjust KPI assumptions.
                      </Typography>
                    )}
                  </Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.replaceExistingZips}
                        onChange={(e) => updateState('replaceExistingZips', e.target.checked)}
                      />
                    }
                    label="Replace existing client zip data (wipe and re-create territories/filters/zips)"
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={handleProjectionFetch}
                      disabled={isLoadingProjection || !state.verticals.selections.length}
                    >
                      {isLoadingProjection ? 'Calculating…' : 'Generate projections'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCopyClientView}
                      disabled={!shareUrl}
                    >
                      Copy client view link
                    </Button>
                    {shareUrl ? (
                      <Stack spacing={0.25}>
                        <Typography color="text.secondary" variant="body2">
                          <a href={shareUrl} target="_blank" rel="noreferrer">
                            Open client view (latest)
                          </a>
                        </Typography>
                        {versionedShareUrl ? (
                          <Typography color="text.secondary" variant="caption">
                            Latest link stays the same; versioned link:{' '}
                            <a href={versionedShareUrl} target="_blank" rel="noreferrer">
                              {versionedShareUrl}
                            </a>
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        Uses pasted/uploaded zips and selected verticals for estimates.
                      </Typography>
                    )}
                  </Stack>

                  {state.projection.summary ? (
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                        {(() => {
                          const summary = state.projection.summary || {};
                          const assumptions = projectionResults.assumptions || {};
                          const totals = projectionResults.totals || {};
                          const budget = Number(summary.budget || totals.budget || 0);
                          const dailyBudget = budget ? budget / 30 : null;
                          const currentScenario = state.projection.scenario || 'base';
                          const leads = Number.isFinite(totals.leads) ? Math.round(totals.leads) : 0;
                          const cpl = totals.avgCpl;
                          const setAppointments = Number.isFinite(totals.setAppointments)
                            ? Math.round(totals.setAppointments)
                            : '—';
                          const issues = Number.isFinite(totals.issues) ? Math.round(totals.issues) : '—';
                          const demos = Number.isFinite(totals.demos) ? Math.round(totals.demos) : '—';
                          const dealsSold = Number.isFinite(totals.netSold) ? Math.round(totals.netSold) : null;
                          const avgJobSize = totals.avgJobSize || assumptions.avg_job_size || null;
                          const netRevenue = Number.isFinite(totals.netRevenue) ? totals.netRevenue : null;
                          const costMarketingPct =
                            totals.costOfMarketingPct != null && Number.isFinite(totals.costOfMarketingPct)
                              ? `${(totals.costOfMarketingPct * 100).toFixed(1)}%`
                              : '—';
                          const setRateLabel = formatRate(assumptions.set_rate, 0);
                          const issueRateLabel = formatRate(assumptions.issue_rate, 0);
                          const demoRateLabel = formatRate(assumptions.demo_rate, 0);
                          const closeRateLabel = formatRate(assumptions.net_close_rate, 0);

                          return [
                            {
                              label: 'Total budget',
                              value: formatCurrency(budget),
                              sub: dailyBudget ? `~${formatCurrency(dailyBudget)}/day` : null,
                            },
                            {
                              label: `Leads & CPL (${currentScenario})`,
                              value: `${Number.isFinite(leads) ? leads : '—'} leads / ${
                                cpl ? formatCurrency(cpl) : '—'
                              }`,
                              sub: 'Est. leads / Blended CPL',
                            },
                            { label: 'Set appointments', value: setAppointments, sub: setRateLabel ? `Set rate ${setRateLabel}` : null },
                            { label: 'Issues', value: issues, sub: issueRateLabel ? `Issue rate ${issueRateLabel}` : null },
                            { label: 'Demos', value: demos, sub: demoRateLabel ? `Demo rate ${demoRateLabel}` : null },
                            { label: 'Net sold', value: dealsSold == null ? '—' : dealsSold.toLocaleString(), sub: closeRateLabel ? `Net close rate ${closeRateLabel}` : null },
                            { label: 'Avg job size', value: avgJobSize ? formatCurrency(avgJobSize) : '—', sub: null },
                            { label: 'Net revenue', value: netRevenue == null ? '—' : formatCurrency(netRevenue), sub: null },
                            { label: 'Cost of marketing', value: costMarketingPct, sub: null },
                          ];
                        })().map((item) => (
                          <Grid item xs={12} sm={6} md={4} key={item.label}>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                              <Typography color="text.secondary" variant="body2">
                                {item.label}
                              </Typography>
                              <Typography variant="h6">{item.value ?? '—'}</Typography>
                              {item.sub ? (
                                <Typography color="text.secondary" variant="caption">
                                  {item.sub}
                                </Typography>
                              ) : null}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>

                      {projectionResults.rows.length ? (
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                          <Table
                            size="small"
                            sx={{
                              tableLayout: 'fixed',
                              '& th, & td': { px: 0.5, py: 0.75, whiteSpace: 'nowrap', fontSize: 12 },
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell>Vertical</TableCell>
                                <TableCell>Budget</TableCell>
                                <TableCell>Leads</TableCell>
                                <TableCell>CPL</TableCell>
                                <TableCell>Set</TableCell>
                                <TableCell>Set%</TableCell>
                                <TableCell>Issues</TableCell>
                                <TableCell>Iss%</TableCell>
                                <TableCell>Demos</TableCell>
                                <TableCell>Demo%</TableCell>
                                <TableCell>Net sold</TableCell>
                                <TableCell>Close%</TableCell>
                                <TableCell>Avg job</TableCell>
                                <TableCell>Net rev</TableCell>
                                <TableCell>CoM%</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {projectionResults.rows.map((row) => {
                                const costMarketingVal =
                                  row.netRevenue && row.budget
                                    ? `${((row.budget / row.netRevenue) * 100).toFixed(1)}%`
                                    : '—';
                                return (
                                  <TableRow key={row.name}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{formatCurrency(row.budget)}</TableCell>
                                    <TableCell>
                                      {Number.isFinite(row.leads) ? Math.round(row.leads) : '—'}
                                    </TableCell>
                                    <TableCell>{row.cpl ? formatCurrency(row.cpl) : '—'}</TableCell>
                                    <TableCell>
                                      {Number.isFinite(row.setAppointments) ? Math.round(row.setAppointments) : '—'}
                                    </TableCell>
                                    <TableCell>{formatRate(row.setRate)}</TableCell>
                                    <TableCell>{Number.isFinite(row.issues) ? Math.round(row.issues) : '—'}</TableCell>
                                    <TableCell>{formatRate(row.issueRate)}</TableCell>
                                    <TableCell>{Number.isFinite(row.demos) ? Math.round(row.demos) : '—'}</TableCell>
                                    <TableCell>{formatRate(row.demoRate)}</TableCell>
                                    <TableCell>{Number.isFinite(row.netSold) ? Math.round(row.netSold) : '—'}</TableCell>
                                    <TableCell>{formatRate(row.netCloseRate)}</TableCell>
                                    <TableCell>{row.avgJobSize ? formatCurrency(row.avgJobSize) : '—'}</TableCell>
                                    <TableCell>{row.netRevenue ? formatCurrency(row.netRevenue) : '—'}</TableCell>
                                    <TableCell>{costMarketingVal}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Paper>
                      ) : null}

                      {territoryRows.length > 0 && (
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                          <Box sx={{ p: 2 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
                              <TaskAltIcon color="success" fontSize="small" />
                              <Typography variant="subtitle2">{territoryRows.length} territories captured</Typography>
                            </Stack>
                            <Table size="small" sx={{ tableLayout: 'auto', minWidth: 800 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Territory</TableCell>
                                  <TableCell>Zip count</TableCell>
                                  <TableCell>Population (raw → filtered)</TableCell>
                                  <TableCell>Median income</TableCell>
                                  <TableCell>Median home value</TableCell>
                                  <TableCell>Min income</TableCell>
                                  <TableCell>Min home value</TableCell>
                                  <TableCell>Min SFH%</TableCell>
                                  <TableCell>Min ownership%</TableCell>
                                  <TableCell>Population retained%</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {territoryRows.map((row, idx) => (
                                  <TableRow
                                    key={`${row.territory}-${idx}`}
                                    title={row.sampleZips.length ? `Sample zips: ${row.sampleZips.join(', ')}` : undefined}
                                  >
                                    <TableCell>{row.territory || 'No territory label'}</TableCell>
                                    <TableCell>
                                      {row.zipCount}
                                      {row.zipCountFiltered != null ? ` → ${row.zipCountFiltered}` : ''}
                                    </TableCell>
                                    <TableCell>
                                      {row.population != null ? row.population.toLocaleString() : '—'} {' \u2192 '}{' '}
                                      {row.populationFiltered != null ? row.populationFiltered.toLocaleString() : '—'}
                                    </TableCell>
                                    <TableCell>
                                      {formatCurrency(row.medianIncomeFiltered ?? row.medianIncome ?? '—')}
                                    </TableCell>
                                    <TableCell>
                                      {formatCurrency(row.medianHomeValueFiltered ?? row.medianHomeValue ?? '—')}
                                    </TableCell>
                                    <TableCell>{formatCurrency(row.minIncome)}</TableCell>
                                    <TableCell>{formatCurrency(row.minHomeValue)}</TableCell>
                                    <TableCell>{formatPercent(row.minSfh, 0)}</TableCell>
                                    <TableCell>{formatPercent(row.minOwnership, 0)}</TableCell>
                                    <TableCell>{formatPercent(row.populationRetainedPct)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {demographics.meta && demographics.meta.filter_found === false && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                No saved client territory zip filter found. Applying default floors based on zip medians.
                              </Alert>
                            )}
                            {demographics.meta &&
                              demographics.meta.filter_found &&
                              demographics.meta.filter_effective === false && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                Zip filter found but did not exclude any rows (no change vs raw).
                              </Alert>
                            )}
                          </Box>
                        </Paper>
                      )}

                      {/* Zip drill-down intentionally hidden for cleaner view */}
                    </Stack>
                  ) : null}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="outlined">Export CSV</Button>
                    <Button variant="contained" onClick={proceedToOnboarding}>
                      Approve projections → Onboarding
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}

PublicProjectionsPage.displayName = 'PublicProjectionsPage';
export default PublicProjectionsPage;
